import { useState, useEffect, useContext } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAppSelector } from '@/store/hooks';
import Routes from '../routes/Routes';
import NavBar from '../components/navigation/NavBar';
import Subheader from '../components/subHeader/subHeader';
import { NotificationProvider } from '../context/notification';
import { Notifications } from '../components/notifications/Notifications';
import {
  getActiveWorkspace,
  getIsRootWorkspace,
  getPathSecondItem,
} from './AppUtilityFunctions';
import SessionContext, { setSessionNavigate } from '../context/SessionProvider';
import { Modal } from '../components/ui/Modal';
import { Button } from '../components/ui/Button';

export default function App() {
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    setSessionNavigate(navigate);
  }, [navigate]);

  const session = useContext(SessionContext);

  const currentUserGroup = useAppSelector((state) => state.workspace.currentUserGroup);
  const allWorkspaces = useAppSelector((state) => state.workspace.allWorkspaces);
  const user = useAppSelector((state) => state.user);
  const oldUserDetails = useAppSelector((state) => state.user.oldUserDetails);

  const [modalOpen, setModalOpen] = useState<string | undefined>();

  const pathSecondItem = getPathSecondItem(location);
  const activeWorkspace = getActiveWorkspace(allWorkspaces, currentUserGroup);
  const isRootWorkspace = getIsRootWorkspace(activeWorkspace);

  const wrapperClass = (() => {
    const parts = location.pathname.split('/');
    if (pathSecondItem === 'welcome' || pathSecondItem === 'project') {
      return 'min-h-screen bg-secondary';
    }
    if (parts.length < 3 || location.pathname.includes('confirm')) {
      return 'flex min-h-screen flex-col';
    }
    return 'min-h-screen bg-background';
  })();

  const showSubheader =
    user?.status === 'ready' &&
    pathSecondItem !== 'welcome' &&
    !isRootWorkspace &&
    session.groupsList?.length > 0;

  return (
    <NotificationProvider>
      <div className={wrapperClass}>
        <NavBar updateModalOpen={setModalOpen} />

        {!session.isAuthenticating && (
          <>
            {showSubheader && (
              <Subheader
                currentItem={session.currentItem}
                location={location}
                shakeAccess={!!activeWorkspace?.shakeAccess}
                user={oldUserDetails}
              />
            )}
            <div className="mx-auto w-full max-w-7xl px-6 py-8 max-md:px-3 max-md:py-4">
              <Routes />
            </div>
          </>
        )}
      </div>

      <Modal open={modalOpen === 'expired'} onClose={() => setModalOpen(undefined)} title="Subscription package has expired" size="sm">
        <Button onClick={() => { navigate('/in/account/billing'); setModalOpen(undefined); }}>Go to billing</Button>
      </Modal>
      <Modal open={modalOpen === 'dataTransfer'} onClose={() => setModalOpen(undefined)} title="Data transfer limit exceeded" size="sm">
        <p className="mb-4 text-sm text-muted-foreground">Additional traffic costs 0.2€ per gigabyte</p>
        <Button onClick={() => { localStorage.setItem('data-transfer-limit-notification-accepted', 'true'); setModalOpen(undefined); }}>OK</Button>
      </Modal>
      <Modal open={modalOpen === 'storageLimit'} onClose={() => setModalOpen(undefined)} title="Storage limit exceeded" size="sm">
        <Button onClick={() => { localStorage.setItem('storage-space-limit-notification-accepted', 'true'); setModalOpen(undefined); }}>OK</Button>
      </Modal>

      {session.isQuerying && (
        <div className="fixed top-0 z-50 h-0.5 w-full bg-accent">
          <div className="h-full animate-pulse bg-primary" style={{ animation: 'expand 3s forwards' }} />
        </div>
      )}

      <Notifications />
    </NotificationProvider>
  );
}
